import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibCalendarClockIcon } from '@chewy/kib-icons-react'
import { KibTooltip } from '@chewy/kib-tooltip-react'

import RhapProblemHistoryPanel from '~/components/dashboard/clients/snapshots-and-records/snapshots/DiagnosesSnapshot/RhapProblemHistoryPanel'
import { getSoapId } from '~/store/reducers/soap'
import type { DiagnosisSnapshotItem, MasterProblemsSnapshotItem } from '~/types'

import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import '@chewy/kib-tag-styles/dist/kib-tag.css'
import styles from './ProblemsHistoryCell.module.scss'

interface ProblemsHistoryCellProps {
  clientId: string
  item: MasterProblemsSnapshotItem | DiagnosisSnapshotItem
  patientId: string
}

const hasChildren = (
  item: MasterProblemsSnapshotItem | DiagnosisSnapshotItem,
): item is MasterProblemsSnapshotItem =>
  'children' in item && Array.isArray(item.children)

const ProblemsHistoryCell = ({
  item,
  clientId,
  patientId,
}: ProblemsHistoryCellProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const currentSoapId = useSelector(getSoapId)

  const shouldShowClockIcon = () => {
    if (!hasChildren(item) || item.children.length === 0) {
      return false
    }

    // Hide if single SOAP and it matches the current SOAP
    if (
      item.children.length === 1 &&
      item.children[0].soapId === currentSoapId
    ) {
      return false
    }

    return true
  }

  return (
    <div className={styles.container}>
      {shouldShowClockIcon() && hasChildren(item) && (
        <>
          <KibButtonNew
            emphasis="tertiary"
            icon={<KibCalendarClockIcon />}
            iconPosition="start"
            ref={buttonRef}
            size="small"
            theme="utility"
          >
            {item.children.length}
          </KibButtonNew>
          <KibTooltip
            className={styles.tooltip}
            placement="bottom"
            popperModifiers={[
              {
                name: 'flip',
                enabled: false,
              },
            ]}
            trigger={buttonRef}
          >
            <div className={styles.tooltipContent}>
              <RhapProblemHistoryPanel
                clientId={clientId}
                patientId={patientId}
                problemLogs={item.children}
              />
            </div>
          </KibTooltip>
        </>
      )}
    </div>
  )
}

export default React.memo(ProblemsHistoryCell)
